import { useContext, useMemo } from "react";
import {
  DEPOSIT_ADDRESS_STATE,
  LimitsReponse,
  OPERATIONS_VALUES,
  PERSON_TYPES,
  REMAINING_LIMITS_CATEGORY,
  TIME_PERIOD_VALUES,
} from "@letsbit/malcolmlb";
import { AlertSign, Button } from "@letsbit/milkylb";
import { IonSkeletonText } from "@ionic/react";
import { ReactComponent as CopyIcon } from "assets/images/icons/copy-purple.svg";
import { FormattedMessage, useIntl } from "react-intl";

import { useFormatter } from "hooks/utils/useFormatter";
import { useGetLimits } from "hooks/payments/useGetLimits";
import { Limits } from "components/Limits";
import MessagesComponent from "components/MessagesWithLinks";
import { TaxWarning } from "components/TaxWarning";
import { messages } from "./messages";
import { DepositPersonalAccountArsProps } from "./types";
import { useCurrency } from "hooks/user/useCurrencies";
import { Pill } from "components/Pill";
import { UserContext } from "providers/UserProvider";

export const DepositPersonalAccountArs: React.FC<
  DepositPersonalAccountArsProps
> = ({
  currencyId,
  paymentNetworkId,
  depositAddress,
  setPaymentNetwork,
  viewerOnly = false,
}) => {
  const currency = useCurrency(currencyId);
  const paymentNetwork = useMemo(() => {
    return currency?.currency_networks.find(
      (cn) => cn.payment_network.id === paymentNetworkId,
    );
  }, [currency?.currency_networks, paymentNetworkId]);
  const { formatMessage } = useIntl();
  const { user } = useContext(UserContext);

  const { data: operationalLimits } = viewerOnly
    ? { data: undefined }
    : useGetLimits({
        currency: currencyId,
        payment_network: paymentNetworkId || "",
        operation: OPERATIONS_VALUES.DEPOSIT,
        category: REMAINING_LIMITS_CATEGORY.OPERATIONAL,
      });

  const operationalMonthlyLimit = useMemo(() => {
    return operationalLimits?.find(
      (l: LimitsReponse) =>
        l.time_period === TIME_PERIOD_VALUES.MONTHLY && l.kind === "volume",
    );
  }, [operationalLimits]);

  const paymentNetworksSelector = useMemo(
    () =>
      currency?.currency_networks
        .filter((cn) => cn.deposit_enabled)
        .map((n) => {
          return { label: n.payment_network.name, value: n };
        }),
    [currency?.currency_networks],
  );

  const { formatValue } = useFormatter();

  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <div className="flex flex-col">
        <h1 className="mb-4 font-['Tomato_Grotesk'] text-3xl font-medium">
          {user?.data.person_type == PERSON_TYPES.JURIDICAL ? (
            <FormattedMessage {...messages.titleOrg} />
          ) : (
            <FormattedMessage {...messages.title} />
          )}
        </h1>
        <div className="flex flex-row items-center gap-x-2">
          {paymentNetworksSelector &&
            paymentNetworksSelector.length > 1 &&
            paymentNetworksSelector?.map((paymentNetwork) => (
              <Pill
                onClick={() => {
                  setPaymentNetwork(paymentNetwork.value);
                }}
                selected={
                  paymentNetworkId === paymentNetwork.value.payment_network.id
                }
              >
                {paymentNetwork.label}
              </Pill>
            ))}
        </div>
        <h3 className="mb-2 text-lg text-[--color-neutral-0]">
          <FormattedMessage {...messages.yourAlias} />
        </h3>
        <div className="mb-4 flex flex-row items-center justify-between gap-x-2">
          {depositAddress.data.state === DEPOSIT_ADDRESS_STATE.CREATED ? (
            <>
              <h2 className="font-['Tomato_Grotesk'] text-4xl font-medium text-[--milkylb-color-neutral-400]">
                {depositAddress.data.cvu_alias}
              </h2>
              <Button
                type="button"
                fill="clear"
                leftIcon={() => <CopyIcon width={16} height={16} />}
                onClick={() =>
                  navigator.clipboard.writeText(depositAddress.data.cvu_alias)
                }
                className="deposit__button letsbit-ui-button-outline-svg-filled ml-6"
              >
                {formatMessage(messages.copy)}
              </Button>
            </>
          ) : (
            <IonSkeletonText animated className="h-12 w-80 rounded-lg" />
          )}
        </div>
        <h3 className="mb-2 text-lg text-[--color-neutral-0]">
          <FormattedMessage {...messages.yourCvu} />
        </h3>
        <div className="mb-4 flex flex-row items-center justify-between gap-x-2">
          {depositAddress.data.state === DEPOSIT_ADDRESS_STATE.CREATED ? (
            <>
              <h2 className="font-['Tomato_Grotesk'] text-4xl font-medium text-[--milkylb-color-neutral-400]">
                {depositAddress.data.address}
              </h2>
              <Button
                type="button"
                fill="clear"
                leftIcon={() => <CopyIcon width={16} height={16} />}
                onClick={() =>
                  navigator.clipboard.writeText(depositAddress.data.address)
                }
                className="deposit__button letsbit-ui-button-outline-svg-filled ml-6"
              >
                {formatMessage(messages.copy)}
              </Button>
            </>
          ) : (
            <IonSkeletonText animated className="h-12 w-[458px] rounded-lg" />
          )}
        </div>
      </div>
      {currency && (
        <>
          {!viewerOnly && (
            <Limits
              limitParams={{
                currency: currency.id,
                payment_network: paymentNetworkId,
                operation: OPERATIONS_VALUES.DEPOSIT,
                category: REMAINING_LIMITS_CATEGORY.OPERATIONAL,
              }}
              period={TIME_PERIOD_VALUES.MONTHLY}
              classname="w-[400px]"
            />
          )}
          {operationalMonthlyLimit && (
            <AlertSign
              text={
                <FormattedMessage
                  {...messages.alertSign}
                  values={{
                    amount: formatValue(
                      operationalMonthlyLimit.value,
                      currency.precision,
                    ),
                    currency: currency.ticker_id.toUpperCase(),
                  }}
                />
              }
            />
          )}
          <MessagesComponent messages={paymentNetwork?.deposit_messages} />
        </>
      )}
      {!viewerOnly && (
        <TaxWarning
          paymentNetworkId={paymentNetworkId}
          tickerId={currency?.ticker_id.toUpperCase() || ""}
          operationType="credit"
        />
      )}
    </div>
  );
};
